import React, { useState, useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'

import { successMessages } from '@/constants/feedbackMessages'
import {
	commentFilterOptions,
	statusFilterOptions,
} from '@/constants/filterQuestions'
import {
	AutocompleteBase,
	Paragraph,
	Tags,
	TagsActions,
	Button,
	MenuQuestions,
	BreadCrumb,
	Loader,
	FilterButton,
	MenuQuestionsMobile,
	SideModalBase,
	Title,
	ModalBase,
	Input,
} from '@components/atoms'
import { FormNewNotebookValue } from '@components/atoms/QuestionNotebook'
import { Question, QuestionPrint } from '@components/molecules'
import { questionsStyles as defaultQuestionsStyles } from '@components/organisms/Questions/style'
import {
	Dismiss20Regular,
	Save24Regular,
	EyeOff24Filled,
	AddCircle20Regular,
} from '@fluentui/react-icons'
import { yupResolver } from '@hookform/resolvers/yup'
import { useIsMobile, usePrintQuestion } from '@hooks'
import useQuestions from '@hooks/useQuestion'
import {
	InitOptionsFilterResponse,
	QuestionType,
} from '@models/questions.model'
import { newFilterClientSchema } from '@schemas/filters/filters.schema'
import { filterAPI } from '@services/FiltersAPI'
import { questionsAPI } from '@services/QuestionsAPI'
import { notificationCreators } from '@store/ducks/notification'
import { useAppDispatch } from '@store/hooks'

import { questionsStyles } from './style'

const {
	container,
	gridFilter,
	tagRow,
	tagsContainer,
	ContainerActionsFilter,
	containerActions,
	buttonSave,
	buttonClear,
	containerButtonNextPage,
	ContainerFocus,
	buttonsFocus,
	showFilter,
	showFilterContainer,
	containerPrint,
} = questionsStyles

const {
	title,
	containerActionsMobile,
	buttonSideMenu,
	tagsSideMenu,
	filterSideMenu,
} = defaultQuestionsStyles

export const QuestionsDiscipline = () => {
	const [searchParams] = useSearchParams()
	const dispatch = useAppDispatch()
	const [showFilters, setShowFilters] = useState(false)
	const [asParams, setAsParams] = useState(true)
	const [openModalCreateFilter, setOpenModalCreateFilter] = useState(false)
	const [initOptions, setInitOptions] = useState<InitOptionsFilterResponse>({
		articles: [],
		disciplines: [],
		institutions: [],
		materId: [],
		subMaterId: [],
		juries: [],
		roles: [],
		status: [],
		years: [],
	})
	const [questions, setQuestions] = useState<Array<QuestionType>>([])
	const [openMenufilter, setOpenMenuFilter] = useState<boolean>(false)
	const [pageFocusMode, setPageFocusMode] = useState(1)
	const [totalQuestions, setTotalQuestions] = useState<number>(0)

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [pageReadMode, setPageReadMode] = useState(1)

	const {
		setValues,
		values,
		clearFilter,
		handleFilterNextPage,
		dataQuestions,
		isSuccessQuestions,
		isLoadingQuestions,
		getQuestions,
	} = useQuestions()

	const isMobile = useIsMobile()

	const [GetAllOptionsFilter, { isSuccess, data }] =
		questionsAPI.useGetAllOptionsFilterMutation()

	const [getOptionsMatter, { data: dataMatter, isSuccess: isSuccessMatter }] =
		questionsAPI.useGetOptionMatterMutation()

	const [
		getOptionsSubMatter,
		{ data: dataSubMatter, isSuccess: isSuccessSubMatter },
	] = questionsAPI.useGetOptionSubMatterMutation()

	const [
		createFilter,
		{ data: dataCreateFilter, isSuccess: isSuccessCreateFilter },
	] = filterAPI.usePostCreateFilterMutation()

	const componentRef = useRef<HTMLDivElement>(null)
	const { handlePrint } = usePrintQuestion({
		componentRef,
		setQuestions,
		questions,
	})

	const {
		register: registerCreateFilter,
		reset: resetCreateFilter,
		handleSubmit: handleSubmitCreateFilter,
		formState: { errors: errorsCreateFilter },
	} = useForm<FormNewNotebookValue>({
		resolver: yupResolver(newFilterClientSchema),
	})

	const onSubmitCreateFilter = handleSubmitCreateFilter((payload) => {
		createFilter({
			name: payload.name,
			filters: {
				discipline: values?.disciplines.map((value) => value.id),
				matter: values?.materId.map((value) => value.id),
				submatter: values.subMaterId.map((value) => value.id),
				year: values.year.map((value) => value.id),
				position: values.office.map((value) => value.id),
				jury: values.juries.map((value) => value.id),
				institution: values.institutions.map((value) => value.id),
				article: [],
			},
		})
	})

	const handleFilter = () => {
		setQuestions([])
		setPageFocusMode(1)
		setPageReadMode(1)
		getQuestions()
	}

	const handleFilterClear = () => {
		setQuestions([])
		setPageFocusMode(1)
		setPageReadMode(0)
		clearFilter()
		setTotalQuestions(0)
		getQuestions(true)
	}

	useEffect(() => {
		GetAllOptionsFilter()
	}, [GetAllOptionsFilter])

	useEffect(() => {
		if (isSuccess && data) {
			setInitOptions(data)
		}
	}, [isSuccess, data])

	useEffect(() => {
		if (values.disciplines.length > 0) {
			getOptionsMatter(values.disciplines.map((value) => value.id).join(','))
		}
	}, [values.disciplines, getOptionsMatter])

	useEffect(() => {
		if (isSuccessMatter && dataMatter) {
			setInitOptions((prevState) => ({
				...prevState,
				materId: dataMatter.data,
			}))

			if (searchParams.get('matter')) {
				setValues((prev) => ({
					...prev,
					materId: dataMatter.data.filter(
						(matter: { id: number; name: string }) =>
							matter.id === Number(searchParams.get('matter'))
					),
				}))
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSuccessMatter, dataMatter])

	useEffect(() => {
		if (isSuccessCreateFilter && dataCreateFilter) {
			resetCreateFilter()
			setOpenModalCreateFilter(false)
			dispatch(notificationCreators.show(successMessages.createdFilter))
		}
	}, [isSuccessCreateFilter, dataCreateFilter, resetCreateFilter, dispatch])

	useEffect(() => {
		if (values.materId?.length > 0) {
			getOptionsSubMatter(values.materId.map((value) => value.id).join(','))
		}
	}, [values.materId, getOptionsSubMatter])

	useEffect(() => {
		if (isSuccessSubMatter && dataSubMatter) {
			setInitOptions((prevState) => ({
				...prevState,
				subMaterId: dataSubMatter.data,
			}))

			if (searchParams.get('subMatter')) {
				setValues((prev) => ({
					...prev,
					subMaterId: dataSubMatter.data.filter(
						(subMatter: { id: number; name: string }) =>
							subMatter.id === Number(searchParams.get('subMatter'))
					),
				}))
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSuccessSubMatter, dataSubMatter])

	useEffect(() => {
		if (isSuccessQuestions && dataQuestions) {
			setQuestions((item) => {
				if (item.length > 0) {
					return [...item, ...dataQuestions.questions[0]]
				}

				return dataQuestions.questions[0]
			})
			setTotalQuestions(dataQuestions.questionTotal)
		}
	}, [isSuccessQuestions, dataQuestions])

	useEffect(() => {
		if (asParams) {
			if (initOptions.disciplines.length > 0) {
				setValues((prev) => ({
					...prev,
					disciplines: initOptions?.disciplines?.filter(
						(disciplines: { id: number; name: string }) =>
							disciplines.id === Number(searchParams.get('discipline'))
					),
				}))

				setAsParams(false)
			} else {
				setValues((prev) => ({
					...prev,
					disciplines: initOptions?.disciplines?.filter(
						(disciplines: { id: number; name: string }) =>
							disciplines.id === Number(searchParams.get('discipline'))
					),
				}))
			}
		}

		if (
			Number(searchParams.get('discipline')) &&
			Number(searchParams.get('matter')) &&
			Number(searchParams.get('subMatter'))
		) {
			if (
				values.disciplines &&
				values.disciplines.length > 0 &&
				values.materId &&
				values.materId.length > 0 &&
				values.subMaterId &&
				values.subMaterId.length > 0
			) {
				handleFilter()

				return
			}
		}

		if (
			Number(searchParams.get('discipline')) &&
			Number(searchParams.get('matter'))
		) {
			if (
				values.disciplines &&
				values.disciplines.length > 0 &&
				values.materId &&
				values.materId.length > 0
			) {
				handleFilter()

				return
			}
		}

		if (Number(searchParams.get('discipline'))) {
			if (values.disciplines && values.disciplines.length > 0) {
				handleFilter()
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [values?.disciplines, values?.materId, values?.subMaterId])

	return (
		<div className={container()}>
			<div>
				<BreadCrumb
					buttonReturn
					locations={[
						{ name: 'Disciplina', link: '/disciplinas' },
						{ name: `Questões`, link: '' },
					]}
				/>
			</div>

			<div className={title()}>
				<Title variant='h1' color='$neutral160'>
					Disciplinas - Questões
				</Title>
			</div>
			{showFilters && (
				<>
					{!isMobile && (
						<div className={gridFilter()}>
							<AutocompleteBase
								name='disciplines'
								onChange={setValues}
								placeholder='Código/Lei'
								selectedText='Código/Lei'
								options={initOptions.disciplines}
								selectedValue={values.disciplines}
							/>
							<AutocompleteBase
								name='materId'
								onChange={setValues}
								placeholder='Assunto'
								selectedText='Assunto'
								options={initOptions.materId}
								selectedValue={values.materId}
							/>
							<AutocompleteBase
								name='subMaterId'
								onChange={setValues}
								placeholder='SubAssunto'
								selectedText='SubAssunto'
								options={initOptions.subMaterId}
								selectedValue={values.subMaterId}
							/>
							<AutocompleteBase
								name='year'
								onChange={setValues}
								placeholder='Ano'
								selectedText='Ano'
								options={initOptions.years}
								selectedValue={values.year}
							/>
							<AutocompleteBase
								name='office'
								onChange={setValues}
								placeholder='Cargo'
								selectedText='Cargo'
								options={initOptions.roles}
								selectedValue={values.office}
							/>
							<AutocompleteBase
								name='juries'
								onChange={setValues}
								placeholder='Banca'
								selectedText='Banca'
								options={initOptions.juries}
								selectedValue={values.juries}
							/>
							<AutocompleteBase
								name='institutions'
								onChange={setValues}
								placeholder='Instituição'
								selectedText='Instituição'
								options={initOptions.institutions}
								selectedValue={values.institutions}
							/>
						</div>
					)}
				</>
			)}

			{!isMobile && (
				<div className={tagsContainer()}>
					{showFilters && (
						<>
							<div className={tagRow()}>
								<Paragraph paragraphType='p2' css={{ color: '$neutral90' }}>
									Comentários:
								</Paragraph>
								<Tags
									name='comment'
									value={values.comment}
									onChange={setValues}
									options={commentFilterOptions}
								/>
							</div>
							<div className={tagRow()}>
								<Paragraph paragraphType='p2' css={{ color: '$neutral90' }}>
									Questões:
								</Paragraph>
								<Tags
									name='quest'
									value={values.quest}
									onChange={setValues}
									options={statusFilterOptions}
								/>
							</div>
							<div className={tagRow()}>
								<Paragraph paragraphType='p2' css={{ color: '$neutral90' }}>
									Modo:
								</Paragraph>
								<Tags
									name='mode'
									value={values.mode}
									onChange={setValues}
									options={[
										{ value: 'random', label: 'Aleatório' },
										{ value: 'order', label: 'Ordenado' },
									]}
								/>
							</div>

							<ContainerActionsFilter
								filter={
									values.disciplines.length > 0 ||
									values.materId.length > 0 ||
									values.subMaterId.length > 0 ||
									values.year.length > 0 ||
									values.office.length > 0 ||
									values.juries.length > 0 ||
									values.institutions.length > 0
								}
							>
								<TagsActions
									title='Código/Lei'
									name='disciplines'
									options={values.disciplines}
									onClose={setValues}
								/>
								<TagsActions
									title='Assunto'
									name='materId'
									options={values.materId}
									onClose={setValues}
								/>
								<TagsActions
									title='SubAssunto'
									name='subMaterId'
									options={values.subMaterId}
									onClose={setValues}
								/>
								<TagsActions
									title='Ano'
									name='year'
									options={values.year}
									onClose={setValues}
								/>
								<TagsActions
									title='Cargo'
									name='office'
									options={values.office}
									onClose={setValues}
								/>
								<TagsActions
									title='Banca'
									name='juries'
									options={values.juries}
									onClose={setValues}
								/>
								<TagsActions
									title='Banca'
									name='institutions'
									options={values.institutions}
									onClose={setValues}
								/>
								<div className={containerActions()}>
									<button
										className={buttonSave()}
										type='button'
										onClick={() => {
											setOpenModalCreateFilter(true)
										}}
									>
										<Save24Regular /> Salvar filtro
									</button>
									<button
										className={buttonClear()}
										type='button'
										onClick={handleFilterClear}
									>
										<Dismiss20Regular /> Limpar filtro
									</button>
									<Button
										onClick={handleFilter}
										loading={isLoadingQuestions}
										css={{ maxWidth: '159px' }}
									>
										Filtrar questões
									</Button>
								</div>
							</ContainerActionsFilter>
						</>
					)}

					<div className={showFilterContainer()}>
						<span
							className={showFilter()}
							onClick={() => setShowFilters(!showFilters)}
						>
							{showFilters ? (
								<>
									<p>Fechar filtros</p>
									<EyeOff24Filled />
								</>
							) : (
								<>
									<p>Mostrar mais filtros</p>
									<AddCircle20Regular />
								</>
							)}
						</span>
					</div>

					<MenuQuestions
						questions={questions}
						setFilter={setValues}
						values={values}
						numberQuestions={isSuccessQuestions ? totalQuestions : 0}
						handlePrint={handlePrint}
					/>
				</div>
			)}

			{isMobile && (
				<>
					<div className={containerActionsMobile()}>
						<Paragraph paragraphType='p2' css={{ color: '$neutral90' }}>
							Total Questões: {questions.length}
						</Paragraph>
						<FilterButton
							onClick={() => {
								setOpenMenuFilter(true)
							}}
							css={{ maxWidth: '126px' }}
						>
							Filtrar
						</FilterButton>
					</div>

					<MenuQuestionsMobile
						questions={questions}
						setFilter={setValues}
						values={values}
						numberQuestions={isSuccessQuestions ? totalQuestions : 0}
						handlePrint={handlePrint}
					/>
				</>
			)}

			{isLoadingQuestions && <Loader />}

			{values.readingMode.value === 1 ? (
				<div style={{ marginTop: '40px' }}>
					{questions.length > 0 &&
						questions.map((value, index) => (
							<Question
								value={value}
								key={index}
								index={index + 1}
								setValue={setQuestions}
							/>
						))}
					{isSuccessQuestions && dataQuestions && questions.length > 0 && (
						<div className={containerButtonNextPage()}>
							<Button
								onClick={handleFilterNextPage}
								loading={isLoadingQuestions}
								disabled={
									questions.length === totalQuestions || isLoadingQuestions
								}
							>
								Carregar mais
							</Button>
						</div>
					)}
				</div>
			) : (
				<div style={{ marginTop: '40px' }}>
					{questions.length > 0 &&
						questions.map((value, index) => (
							<ContainerFocus key={index} open={pageFocusMode === index + 1}>
								<Question
									value={value}
									index={index + 1}
									setValue={setQuestions}
								/>
							</ContainerFocus>
						))}

					{questions.length > 0 && (
						<div className={buttonsFocus()}>
							<Button
								outlined
								css={{ maxWidth: '159px' }}
								disabled={pageFocusMode <= 1}
								onClick={() => {
									setPageFocusMode((prev) => {
										if (prev > 1) {
											return prev - 1
										}

										return prev
									})
								}}
							>
								Anterior
							</Button>
							<Button
								outlined
								css={{ maxWidth: '159px' }}
								onClick={() => {
									if (pageFocusMode >= questions.length) {
										handleFilterNextPage()
									} else {
										setPageFocusMode((prev) => prev + 1)
									}
								}}
								disabled={pageFocusMode > questions.length}
							>
								Próximo
							</Button>
						</div>
					)}
				</div>
			)}

			<div className={containerPrint()}>
				<div ref={componentRef}>
					<img alt='logo' src='/assets/svg/logo.svg' />

					{questions.length > 0 &&
						questions.map((value, index) => (
							<QuestionPrint value={value} key={index} index={index + 1} />
						))}
				</div>
			</div>

			<ModalBase
				title='Criar filtro'
				open={openModalCreateFilter}
				onOpenChange={setOpenModalCreateFilter}
				variant='checkBlue'
			>
				<form onSubmit={onSubmitCreateFilter} style={{ width: '100%' }}>
					<Input
						placeholder='Digite o nome do filtro...'
						error={errorsCreateFilter.name?.message}
						{...registerCreateFilter('name')}
						css={{ marginBottom: '34px', marginTop: '32px' }}
					/>
					<div style={{ display: 'flex', gap: '26px', marginTop: '34px' }}>
						<Button>Salvar</Button>
						<Button
							outlined
							onClick={() => {
								setOpenModalCreateFilter(!openModalCreateFilter)
								resetCreateFilter()
							}}
						>
							Cancelar
						</Button>
					</div>
				</form>
			</ModalBase>

			<SideModalBase
				name='Filtros'
				open={openMenufilter}
				setOpen={setOpenMenuFilter}
			>
				<>
					<div className={filterSideMenu()}>
						<AutocompleteBase
							name='disciplines'
							onChange={setValues}
							placeholder='Código/Lei'
							selectedText='Código/Lei'
							options={initOptions.disciplines}
							selectedValue={values.disciplines}
						/>
						<AutocompleteBase
							name='materId'
							onChange={setValues}
							placeholder='Assunto'
							selectedText='Assunto'
							options={initOptions.materId}
							selectedValue={values.materId}
						/>
						<AutocompleteBase
							name='subMaterId'
							onChange={setValues}
							placeholder='SubAssunto'
							selectedText='SubAssunto'
							options={initOptions.subMaterId}
							selectedValue={values.subMaterId}
						/>
						<AutocompleteBase
							name='year'
							onChange={setValues}
							placeholder='Ano'
							selectedText='Ano'
							options={initOptions.years}
							selectedValue={values.year}
						/>
						<AutocompleteBase
							name='office'
							onChange={setValues}
							placeholder='Cargo'
							selectedText='Cargo'
							options={initOptions.roles}
							selectedValue={values.office}
						/>
						<AutocompleteBase
							name='juries'
							onChange={setValues}
							placeholder='Banca'
							selectedText='Banca'
							options={initOptions.juries}
							selectedValue={values.juries}
						/>
						<AutocompleteBase
							name='institutions'
							onChange={setValues}
							placeholder='Instituição'
							selectedText='Instituição'
							options={initOptions.institutions}
							selectedValue={values.institutions}
						/>
					</div>
					<div className={tagsSideMenu()}>
						<TagsActions
							title='Código/Lei'
							name='disciplines'
							options={values.disciplines}
							onClose={setValues}
						/>
						<TagsActions
							title='Assunto'
							name='materId'
							options={values.materId}
							onClose={setValues}
						/>
						<TagsActions
							title='SubAssunto'
							name='subMaterId'
							options={values.subMaterId}
							onClose={setValues}
						/>
						<TagsActions
							title='Ano'
							name='year'
							options={values.year}
							onClose={setValues}
						/>
						<TagsActions
							title='Cargo'
							name='office'
							options={values.office}
							onClose={setValues}
						/>
						<TagsActions
							title='Banca'
							name='juries'
							options={values.juries}
							onClose={setValues}
						/>
						<TagsActions
							title='Instituição'
							name='institutions'
							options={values.institutions}
							onClose={setValues}
						/>
					</div>
					<div className={tagsSideMenu()}>
						<div className={tagRow()}>
							<Paragraph paragraphType='p2' css={{ color: '$neutral90' }}>
								Comentários:
							</Paragraph>
							<Tags
								name='comment'
								value={values.comment}
								onChange={setValues}
								options={commentFilterOptions}
							/>
						</div>
						<div className={tagRow()}>
							<Paragraph paragraphType='p2' css={{ color: '$neutral90' }}>
								Questões:
							</Paragraph>
							<Tags
								name='quest'
								value={values.quest}
								onChange={setValues}
								options={statusFilterOptions}
							/>
						</div>
						<div className={tagRow()}>
							<Paragraph paragraphType='p2' css={{ color: '$neutral90' }}>
								Modo:
							</Paragraph>
							<Tags
								name='mode'
								value={values.mode}
								onChange={setValues}
								options={[
									{ value: 'random', label: 'Aleatório' },
									{ value: 'order', label: 'Ordenado' },
								]}
							/>
						</div>
					</div>

					<div className={buttonSideMenu()}>
						<Button
							onClick={() => {
								handleFilter()
								setOpenMenuFilter(false)
							}}
							loading={isLoadingQuestions}
						>
							Filtrar
						</Button>
						<Button outlined onClick={handleFilterClear}>
							Limpar filtro
						</Button>
					</div>
				</>
			</SideModalBase>
		</div>
	)
}
